import _ from "lodash";
import * as Yup from "yup";
import {
  BinaryBooleanOperator,
  UnaryBooleanOperator,
  type FormkitComponent,
  type FormkitNode,
  type FormkitReference,
  type FormkitSection,
  type Option,
  type RadioGroupOption,
} from "../../api";
import { Boolean as FormkitBoolean } from "../booleans";
import { ShowModifier, switchOnStateKey } from "../modifiers";
import { ContextReference } from "../references";
import { Message, RadioGroup } from "./../components";
import { Section } from "./../layouts";
import { CustomSegmentNameSection } from "./common";
import {
  DEFAULT_DELETE_METADATA,
  DEFAULT_MODE_METADATA,
  DELETE_MODE_ORDER,
  JOURNEYS_COHORT_MODE_METADATA,
  JOURNEYS_TRIGGER_MODE_METADATA,
  MODE_ORDER,
} from "./constants";

const isDefaultMode = (prop: any): prop is keyof typeof DEFAULT_MODE_METADATA =>
  Boolean(DEFAULT_MODE_METADATA[prop]);

const isNotJourneySync = FormkitBoolean({
  operator: UnaryBooleanOperator.Not,
  operand: ContextReference({
    key: "journeySettings.mode",
  }),
});

const isJourneyCohortMode = FormkitBoolean({
  leftOperand: ContextReference({
    key: "journeySettings.mode",
  }),
  operator: BinaryBooleanOperator.Equals,
  rightOperand: "cohort",
});

const isJourneyTriggerMode = FormkitBoolean({
  leftOperand: ContextReference({
    key: "journeySettings.mode",
  }),
  operator: BinaryBooleanOperator.Equals,
  rightOperand: "trigger",
});

export function ModeSection(
  props: {
    options:
      | (keyof typeof DEFAULT_MODE_METADATA | RadioGroupOption)[]
      | FormkitReference;
    validation?: any;
    default?: any;
    heading?: string;
    subheading?: string;
    key?: string;
  },
  siblings?: FormkitNode[],
): FormkitSection {
  const defaultHeading =
    "How do you want to update the records in {{ destinationDefinition.name | default: 'the destination' }}?";

  // If props.options is a reference, return early since we don't need to make any changes to props.options
  if (!Array.isArray(props.options)) {
    return Section({
      heading: props.heading || defaultHeading,
      subheading: props.subheading,
      children: [
        // TODO: need better way to handle props.options being a FormkitReference
        RadioGroup(props.key ?? "mode", {
          validation: Yup.string().required(),
          ...props,
          options: props.options,
        }),
      ],
    });
  }

  let options;
  const modes = props.options.map((option) =>
    isDefaultMode(option) ? option : option.value,
  ) as string[];
  const defaultValidation = Yup.string().oneOf(modes).required();

  options = props.options.map((option) => {
    if (isDefaultMode(option)) {
      return DEFAULT_MODE_METADATA[option];
    }
    return option;
  });
  options = _.sortBy(options, (opt) => {
    return MODE_ORDER[opt.value] ?? Object.keys(MODE_ORDER).length;
  });

  return Section({
    heading: props.heading || defaultHeading,
    subheading: props.subheading,
    children: [
      ShowModifier({
        condition: isNotJourneySync,
        children: [
          RadioGroup(props.key ?? "mode", {
            validation: defaultValidation,
            ...props,
            options,
          }),
        ],
      }),
      ShowModifier({
        condition: isJourneyCohortMode,
        children: [
          RadioGroup(props.key ?? "mode", {
            validation: defaultValidation,
            ...props,
            options: getJourneyModeOptions(props.options, "cohort"),
          }),
        ],
      }),
      ShowModifier({
        condition: isJourneyTriggerMode,
        children: [
          RadioGroup(props.key ?? "mode", {
            validation: defaultValidation,
            ...props,
            options: getJourneyModeOptions(props.options, "trigger"),
          }),
        ],
      }),
      ...(siblings ?? []),
    ],
  });
}

const getJourneyModeOptions = (
  options: (keyof typeof DEFAULT_MODE_METADATA | RadioGroupOption)[],
  mode: "cohort" | "trigger",
) => {
  const metadata =
    mode === "cohort"
      ? JOURNEYS_COHORT_MODE_METADATA
      : JOURNEYS_TRIGGER_MODE_METADATA;
  return options.map((option) => {
    if (isDefaultMode(option)) {
      return metadata[option];
    } else {
      return {
        ...option,
        description:
          metadata[String(option.value)]?.description ?? option.description,
        badge: metadata[String(option.value)]?.badge,
      };
    }
  });
};

const isDefaultDeleteMode = (
  prop: any,
): prop is keyof typeof DEFAULT_DELETE_METADATA =>
  Boolean(DEFAULT_DELETE_METADATA[prop]);

export function DeleteModeSection(props: {
  options: (keyof typeof DEFAULT_DELETE_METADATA | RadioGroupOption)[];
  heading?: string;
  subheading?: string;
  defaultValue?: string;
}): FormkitSection {
  const additionalOptions = props.options.map((option) => {
    if (isDefaultDeleteMode(option)) {
      return DEFAULT_DELETE_METADATA[option];
    }
    return option;
  });

  const options = _.sortBy(additionalOptions, (option) => {
    return (
      DELETE_MODE_ORDER[String(option.value)] ??
      Object.keys(DELETE_MODE_ORDER).length
    );
  });

  options.unshift({
    label: "Do nothing",
    value: undefined,
    description: undefined,
  });

  return Section({
    heading:
      props.heading ||
      "How would you like to handle records leaving the query result?",
    subheading:
      props.subheading ||
      `Warning: deleting records or clearing fields may delete data
          that was not authored by this Hightouch sync`,
    children: [
      ShowModifier({
        condition: isNotJourneySync,
        children: [
          RadioGroup("deleteMode", {
            options,
            default: props.defaultValue,
          }),
        ],
      }),
      ShowModifier({
        condition: isJourneyTriggerMode,
        children: [
          Message({
            title: "Journeys behavior",
            message:
              "No removals occur when using Trigger mode within a journey.",
            variant: "info",
          }),
          RadioGroup("deleteMode", {
            options,
            default: props.defaultValue,
            disable: true,
          }),
        ],
      }),
      ShowModifier({
        condition: isJourneyCohortMode,
        children: [
          RadioGroup("deleteMode", {
            options: options.map((option) => {
              if (option.label === "Do nothing") {
                return {
                  ...option,
                  badge: {
                    text: "Not recommended",
                    variant: "warning",
                    icon: "Warning",
                  },
                  description:
                    "Records exiting the journey will not be removed.",
                };
              }
              return option;
            }),
            default: props.defaultValue,
          }),
        ],
      }),
    ],
  });
}

export function OrmSegmentSection(props: {
  resource?: string;
  /**
   * @default CustomSegmentNameSection
   */
  newSegmentComponents?: FormkitNode[];
  existingComponent?: FormkitComponent;
  existingComponentSubHeading?: string;
  options?: ("none" | "create" | "existing" | Option)[];
  validation?: any;
  heading?: string;
  subheading?: string;
}): FormkitNode[] {
  const name = props.resource || "audience";
  const segmentModeOptions: Option[] = [];
  const defaultSegmentOptions = [
    {
      label: `Don't add to ${name}`,
      value: undefined,
    },
    { label: `Create a new ${name}`, value: "create" },
    {
      label: `Use an existing ${name}`,
      value: "existing",
    },
  ];
  if (!props.options) {
    segmentModeOptions.push(...defaultSegmentOptions);
  } else {
    for (const option of props.options) {
      if (typeof option === "object") {
        segmentModeOptions.push(option);
        continue;
      }
      switch (option) {
        case "none":
          segmentModeOptions.push({
            label: `Don't add to ${name}`,
            value: undefined,
          });
          break;
        case "create":
          segmentModeOptions.push({
            label: `Create a new ${name}`,
            value: "create",
          });
          break;
        case "existing":
          segmentModeOptions.push({
            label: `Use an existing ${name}`,
            value: "existing",
          });
          break;
        default:
          // Unsupported default segment option
          break;
      }
    }
  }

  return [
    Section({
      heading:
        props?.heading ||
        `Would you like to add these records to an existing or new ${name}?`,
      subheading:
        props?.subheading ||
        `To test individual rows, use an existing ${name} or don't add to a ${name}.`,
      children: [
        RadioGroup("segmentMode", {
          options: segmentModeOptions,
        }),
      ],
    }),
    switchOnStateKey("segmentMode", [
      {
        value: "existing",
        children: [
          Section({
            heading: `Which existing ${name} would you like to sync?`,
            subheading: props?.existingComponentSubHeading,
            children: props?.existingComponent ? [props.existingComponent] : [],
          }),
        ],
      },
      {
        value: "create",
        children: props.newSegmentComponents || [
          CustomSegmentNameSection(props),
        ],
      },
    ]),
  ];
}
